import React, { useState } from 'react';

/**
 * An avatar we host comes back as an API-relative path, and the client is not
 * always on the API's origin — in dev it is :3000 against :8002, and in
 * production the app and the API can be deployed separately. Resolving it
 * against the API base is what makes a stored picture load at all; a GCS or
 * Google URL is already absolute and passes through untouched.
 */
const API_ORIGIN = (process.env.REACT_APP_API_SERVER_URL || '').trim().replace(/\/$/, '');

export function resolveAvatarSrc(src) {
  if (!src) return null;
  if (/^https?:\/\//i.test(src) || src.startsWith('data:')) return src;
  return API_ORIGIN ? `${API_ORIGIN}${src}` : src;
}

/**
 * A person, drawn as their picture — or their initials when there isn't one.
 *
 * Initials were all this product ever drew, because the users table had no
 * column for a picture, and the photo every Google sign-in arrives with sat
 * unread in auth_provider_info. This prefers the picture and falls back on
 * its own: a URL that 404s (a deleted object, a Google photo that has moved)
 * lands on the initials rather than a broken-image glyph.
 */
export function initialsOf(name) {
  if (!name) return '?';
  return String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');
}

export default function Avatar({ name, src, size = 24, className = '' }) {
  // Keyed on src so replacing a broken picture with a good one clears the
  // fallback instead of showing initials forever.
  const [failedSrc, setFailedSrc] = useState(null);
  const failed = failedSrc != null && failedSrc === src;
  const dim = `${size}px`;
  const resolved = resolveAvatarSrc(src);
  const showImage = resolved && !failed;

  if (showImage) {
    return (
      <img
        src={resolved}
        alt=""
        title={name || undefined}
        /* Google serves a profile photo only when no referrer is sent: the
           same URL answers 200 with none and 429 with one, so without this
           every Google user's picture silently failed to the initials it was
           meant to replace. */
        referrerPolicy="no-referrer"
        onError={() => setFailedSrc(src)}
        className={`inline-block flex-none rounded-full object-cover ${className}`}
        style={{ width: dim, height: dim }}
      />
    );
  }

  return (
    <span
      aria-hidden
      title={name || undefined}
      className={`inline-flex flex-none items-center justify-center rounded-full bg-surface-hover font-semibold text-[color:var(--text-secondary)] ${className}`}
      style={{ width: dim, height: dim, fontSize: Math.max(9, Math.round(size * 0.4)) }}
    >
      {initialsOf(name)}
    </span>
  );
}
